import { LOCATIONS_URL, PHYSICIANS_URL } from './constants';
import mapPageInformation from './map-page-information';
import { OasAppContextProps } from '@/types/OasAppContextTypes';
import { Pages, RouteSegments } from '@/types/Routing';
import { LinkFieldValue } from '@sitecore-jss/sitecore-jss-nextjs';
import { buildListingUrlWithFilters } from '@/features/listing/utils';
import { ListingRequest } from '@/features/listing/listing.types';

/**
 * Generates breadcrumb trail based on the current pathname
 * Handles different route structures for Sitecore and DotCMS, including location market contexts
 *
 * @param t - Translation function
 * @param state - Application state without breadcrumbs
 * @param pathname - Current URL pathname
 * @param appliedFilters - Optional applied filters to preserve in listing breadcrumb links
 * @returns Array of breadcrumb items with text and href
 */
export const getBreadcrumbsFromPath = (
  t: (key: string) => string,
  state: Omit<OasAppContextProps, 'breadcrumbs'>,
  pathname: string,
  appliedFilters?: Partial<ListingRequest>
): LinkFieldValue[] => {
  let splitPath = pathname.split('/').filter((p) => p);

  const mappedPageInfo = mapPageInformation(t, state);
  const breadcrumbs: LinkFieldValue[] = [
    { text: mappedPageInfo.root.breadcrumb, href: mappedPageInfo.root.href },
  ];

  /**
   * Route structures:
   * 1. Sitecore (main market site):
   *    - /physicians/listing
   *    - /physicians/specialty/specialty-name/listing
   *    - /physicians/profile/doctor-name
   *    - /physicians/booking/doctor-name
   *    - /physicians/confirmation/booking-id
   *
   * 2. Sitecore (location market context):
   *    - /locations/location-name/physicians/listing
   *    - /locations/location-name/physicians/specialty/specialty-name/listing
   *    - /locations/location-name/physicians/profile/doctor-name
   *    - /locations/location-name/physicians/booking/doctor-name
   *    - /locations/location-name/physicians/confirmation/booking-id
   *
   * 3. DotCMS (main market site):
   *    - /physicians/listing
   *    - /physicians/specialty/specialty-name/listing
   *    - /physicians/profile/doctor-name
   *    - /physicians/booking/doctor-name
   *    - /physicians/confirmation/booking-id
   *
   * 4. DotCMS (location market context):
   *    - /physicians/locations/location-name/listing
   *    - /physicians/locations/location-name/specialty/specialty-name/listing
   *    - /physicians/locations/location-name/profile/doctor-name
   *    - /physicians/locations/location-name/booking/doctor-name
   *    - /physicians/locations/location-name/confirmation/booking-id
   */

  // Determine if this is a location market context and extract location prefix
  let locationPrefix = '';
  let physiciansIndex = -1;
  let specialtyPathFragment = ''; // Will store "/specialty/specialty-name" if present

  const locationsSegment = RouteSegments.Locations; // 'locations'
  const physiciansSegment = Pages.Physicians; // 'physicians'

  // Normalize path: If physicians is missing (due to basePath), add it back
  // For Sitecore with basePath='/physicians', pathname won't include 'physicians'
  // Example: /profile/doctor-name needs to become ['physicians', 'profile', 'doctor-name']
  if (splitPath[0] !== locationsSegment && splitPath[0] !== physiciansSegment) {
    // Path doesn't start with locations or physicians, so prepend physicians
    splitPath = [physiciansSegment, ...splitPath];
  }

  // Sitecore location context: /locations/location-name/physicians/...
  const isSitecoreLocationContext =
    splitPath[0] === locationsSegment && splitPath.includes(physiciansSegment);

  // DotCMS location context: /physicians/locations/location-name/...
  const isDotCMSLocationContext =
    splitPath[0] === physiciansSegment && splitPath[1] === locationsSegment;

  if (isSitecoreLocationContext) {
    // Find physicians after the locations segment
    physiciansIndex = splitPath.findIndex(
      (segment, idx) =>
        segment === physiciansSegment && idx > 1 && splitPath[idx - 2] === locationsSegment
    );

    if (physiciansIndex > 1) {
      // Extract location prefix: /locations/location-name
      const locationName = splitPath[1];
      locationPrefix = `${LOCATIONS_URL}/${locationName}`;
    }
  } else if (isDotCMSLocationContext) {
    // DotCMS location context
    const locationName = splitPath[2];
    locationPrefix = `${PHYSICIANS_URL}${LOCATIONS_URL}/${locationName}`;
    physiciansIndex = 0; // physicians is at index 0
  } else {
    // Standard case: physicians should be first segment (main market site)
    physiciansIndex = splitPath.indexOf(physiciansSegment);
  }

  if (physiciansIndex === -1) {
    // Fallback: just return root if 'physicians' not found
    return breadcrumbs;
  }

  // Check for specialty segment: /physicians/specialty/specialty-name/...
  // Pattern: physicians -> specialty -> specialty-name -> (listing|profile|booking|confirmation)
  const routeAfterPhysicians = splitPath[physiciansIndex + 1];
  if (routeAfterPhysicians === RouteSegments.Specialty) {
    const specialtyName = splitPath[physiciansIndex + 2];
    if (specialtyName) {
      specialtyPathFragment = `/${RouteSegments.Specialty}/${specialtyName}`;
    }
  }

  // Helper to add location prefix and specialty to hrefs
  const getHrefWithLocation = (baseHref: string): string => {
    let href = baseHref;

    // Add specialty segment if present (before the page name)
    if (specialtyPathFragment) {
      // Insert specialty segment after /physicians and before the page
      // Example: /physicians/listing -> /physicians/specialty/cardiac-surgery/listing
      href = href.replace(`/${Pages.Listing}`, `${specialtyPathFragment}/${Pages.Listing}`);
    }

    // Add location prefix if present
    if (!locationPrefix) {
      // Add filters to listing page href if available
      if (href.includes(`/${Pages.Listing}`) && appliedFilters) {
        href = buildListingUrlWithFilters(href, appliedFilters);
      }
      return href;
    }

    // For Sitecore location context: /locations/location-name + /physicians/page
    // For DotCMS location context: replace /physicians with /physicians/locations/location-name
    if (isSitecoreLocationContext) {
      href = `${locationPrefix}${href}`;
    } else {
      // DotCMS: replace /physicians with the full location prefix
      href = href.replace(PHYSICIANS_URL, locationPrefix);
    }

    // Add filters to listing page href if available
    if (href.includes(`/${Pages.Listing}`) && appliedFilters) {
      href = buildListingUrlWithFilters(href, appliedFilters);
    }

    return href;
  };

  // Add physicians breadcrumb with location-aware href
  breadcrumbs.push({
    text: mappedPageInfo.physicians.breadcrumb,
    href: getHrefWithLocation(mappedPageInfo.physicians.href),
  });

  // Get the route segment after 'physicians' (or after specialty if present)
  // If specialty exists: physicians -> specialty -> specialty-name -> routeSegment
  // Otherwise: physicians -> routeSegment
  let routeSegment = splitPath[physiciansIndex + 1];

  if (specialtyPathFragment) {
    // Skip past specialty and specialty-name to get to the actual page route
    routeSegment = splitPath[physiciansIndex + 3];
  }

  if (!routeSegment) {
    // Only /physicians in path (or /physicians/specialty/name without final page)
    return breadcrumbs;
  }

  // Build breadcrumb trail based on route segment using mapPageInformation
  const pageKeys = [Pages.Listing, Pages.Profile, Pages.Booking, Pages.Confirmation] as const;

  if (pageKeys.includes(routeSegment as (typeof pageKeys)[number])) {
    const currentRoute = routeSegment as (typeof pageKeys)[number];

    // Build hierarchical breadcrumb trail with location-aware hrefs
    switch (currentRoute) {
      case Pages.Listing:
        // physicians -> listing
        breadcrumbs.push({
          text: mappedPageInfo.listing.breadcrumb,
          href: getHrefWithLocation(mappedPageInfo.listing.href),
        });
        break;

      case Pages.Profile:
        // physicians -> listing -> profile
        breadcrumbs.push({
          text: mappedPageInfo.listing.breadcrumb,
          href: getHrefWithLocation(mappedPageInfo.listing.href),
        });
        breadcrumbs.push({
          text: mappedPageInfo.profile.breadcrumb,
          href: getHrefWithLocation(mappedPageInfo.profile.href),
        });
        break;

      case Pages.Booking:
        // physicians -> listing -> profile -> booking
        breadcrumbs.push({
          text: mappedPageInfo.listing.breadcrumb,
          href: getHrefWithLocation(mappedPageInfo.listing.href),
        });
        breadcrumbs.push({
          text: mappedPageInfo.profile.breadcrumb,
          href: getHrefWithLocation(mappedPageInfo.profile.href),
        });
        breadcrumbs.push({
          text: mappedPageInfo.booking.breadcrumb,
          href: getHrefWithLocation(mappedPageInfo.booking.href),
        });
        break;

      case Pages.Confirmation:
        // physicians -> listing -> profile -> booking -> confirmation
        breadcrumbs.push({
          text: mappedPageInfo.listing.breadcrumb,
          href: getHrefWithLocation(mappedPageInfo.listing.href),
        });
        breadcrumbs.push({
          text: mappedPageInfo.profile.breadcrumb,
          href: getHrefWithLocation(mappedPageInfo.profile.href),
        });
        breadcrumbs.push({
          text: mappedPageInfo.booking.breadcrumb,
          href: getHrefWithLocation(mappedPageInfo.booking.href),
        });
        breadcrumbs.push({
          text: mappedPageInfo.confirmation.breadcrumb,
          href: getHrefWithLocation(mappedPageInfo.confirmation.href),
        });
        break;
    }
  }

  return breadcrumbs;
};
